import React, { useEffect, useState } from 'react';
import { InstallSDKStep } from './InstallSDK/InstallSDK';
import { InstallationInProgressStep } from './InstallationInProgress';
import { SetupCompleteTransition } from './SetupCompleteTransition';
import { useAccountDataPolling } from '../setupFlowManager';
import useAnalytics from '../../../hooks/useAnalytics';
import useAccountInfo from '../../../hooks/useAccountInfo';
import { EventName } from '../../../analytics';

const STEP_DELAY = 2000;

interface StatusItem {
  label: string;
  statusKey: 'hasSession' | 'hasDeclarations' | 'hasMetrics';
  tip?: React.ReactNode;
  timeoutTip?: React.ReactNode;
  completedEventName: EventName;
}

export const statusItems: StatusItem[] = [
  {
    label: 'Recognized your service',
    statusKey: 'hasSession',
    completedEventName: 'sdk_setup_screen_session_initiated',
  },
  {
    label: 'Mapping your code',
    statusKey: 'hasDeclarations',
    completedEventName: 'sdk_setup_screen_declarations_received',
    timeoutTip: (
      <>
        This takes longer than expected. Make sure <code>register()</code> is called at
        the top of your entry file to enable proper mapping.
        <p>
          View the{' '}
          <a
            href="https://docs.hud.io/docs/node-sdk-ie"
            target="_blank"
            rel="noopener noreferrer"
          >
            troubleshooting guide
          </a>
          .
        </p>
      </>
    ),
  },
  {
    label: 'Receiving data',
    statusKey: 'hasMetrics',
    completedEventName: 'sdk_setup_screen_data_received',
    tip: <>Make sure your backend service is triggered so we can detect its behavior.</>,
    timeoutTip: (
      <>
        This takes longer than expected. Make sure your backend service is triggered so we
        can detect its behavior.
        <p>
          View the{' '}
          <a
            href="https://docs.hud.io/docs/node-sdk-ie"
            target="_blank"
            rel="noopener noreferrer"
          >
            troubleshooting guide
          </a>
          .
        </p>
      </>
    ),
  },
];

export default function InstallationSetupStepContent() {
  const analytics = useAnalytics();

  const { startPolling, stopPolling } = useAccountDataPolling();
  const { data: accountInfoData } = useAccountInfo();

  // currentStep is the index of the current completed step,
  // -1 means no step has been completed yet
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [desiredStep, setDesiredStep] = useState<number>(-1);
  const [complete, setComplete] = useState<boolean>(false);
  const [secondsSinceStepStart, setSecondsSinceStepStart] = useState<number>(0);

  useEffect(() => {
    startPolling();
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (accountInfoData) {
      // iterate through the status items and find the index of the last item that its statusKey exists in accountInfoData,
      const desiredStepFromData = findLastIndex(
        statusItems,
        (statusItem: StatusItem) => accountInfoData[statusItem.statusKey],
      );
      if (desiredStepFromData !== -1) {
        setDesiredStep(desiredStepFromData);
      }
    }
  }, [accountInfoData]);

  // staggered updates of the step indexes
  useEffect(() => {
    // set an interval every STEP_DELAY milliseconds to update the currentStepIndex
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (desiredStep !== null) {
        // increment the currentStepIndex until it reaches the desiredStep
        setCurrentStep((currentStepIndex) => {
          if (currentStepIndex === desiredStep) {
            return currentStepIndex;
          }
          return (currentStepIndex ?? -1) + 1;
        });
      }
    }, STEP_DELAY);
    if (document.visibilityState === 'hidden') {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [desiredStep]);

  useEffect(() => {
    analytics.capture(statusItems[currentStep]?.completedEventName);
    let completeTimeout = null;
    setSecondsSinceStepStart(0);
    const stepTimerTimeout = setInterval(() => {
      setSecondsSinceStepStart((secondsSinceStepStart) => secondsSinceStepStart + 1);
    }, 1000);

    if (currentStep !== null && currentStep >= statusItems.length - 1) {
      stopPolling();
      completeTimeout = setTimeout(() => {
        setComplete(true);
      }, STEP_DELAY);
    }
    return () => {
      clearInterval(stepTimerTimeout);
      if (completeTimeout) {
        clearTimeout(completeTimeout);
      }
    };
  }, [currentStep]);

  if (!accountInfoData?.hasSession) {
    return <InstallSDKStep />;
  } else if (!complete) {
    return (
      <InstallationInProgressStep
        installationSetupStep={currentStep}
        secondsSinceStepStart={secondsSinceStepStart}
      />
    );
  } else {
    return <SetupCompleteTransition />;
  }
}

/**
 * Returns the index of the last element in the array where predicate is true, and -1
 * otherwise.
 * @param array The source array to search in
 * @param predicate find calls predicate once for each element of the array, in descending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
 */
export function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean,
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}
